import { KnownClearingHouses } from './BillingCode.type';
import { ConnectorRequestOperation, IntegrationDocumentType } from './IntegrationConnector.type';

/**
 * Connector tracing shapes (plan §3g) — shared by the server-side writer,
 * the connection-api trace endpoints, and the admin ops console (defensive
 * typing across the call chain; no inline shapes).
 */

export type ConnectorTraceDirection = 'inbound' | 'outbound';

/**
 * Trace steps that are not vendor operations: the ops-console feeds (rows the
 * console queries BY this value) and the non-operation flows. Same grammar as
 * `ConnectorRequestOperation` — `noun.verb` — so the Step column reads as one
 * vocabulary. A step is one of these or an operation; never a bare string.
 */
export enum ConnectorFeedStep {
  InboundDrain = 'inbound.drain',
  InboundIngest = 'inbound.ingest',
  InboundWebhook = 'inbound.webhook',
  CredentialKey = 'credential.key',
  ConsoleError = 'console.error',
  ConsoleRun = 'console.run',
  ConsoleOperation = 'console.operation',
  ConsoleConfig = 'console.config',
  ConsoleReviewed = 'console.reviewed',
}

export type ConnectorTraceStep = ConnectorRequestOperation | ConnectorFeedStep;

export type ConnectorTraceFilters = {
  clinicIds?: number[];
  docTypes?: IntegrationDocumentType[];
  direction?: ConnectorTraceDirection;
};

export type ConnectorTraceConfig = {
  filters?: ConnectorTraceFilters;
  /** Session active while traceUntil > now (epoch ms); 0 = disabled. */
  traceUntil: number;
};

export type ConnectorTraceEntry = {
  clinicId: number;
  vendor: KnownClearingHouses;
  direction?: ConnectorTraceDirection | null;
  docType?: IntegrationDocumentType | null;
  exchangeId?: string | null;
  step: ConnectorTraceStep;
  message: string;
  /** PHI-FREE: identifiers, decisions, durations — never patient data. */
  data?: Record<string, unknown> | null;
  /**
   * What the traffic behind this entry IS (from the resolved credentials'
   * key, INFRA-11): `mock` persists `data` unscrubbed — a test-mode key
   * never carries real patient data; `phi`/absent scrubs as always.
   */
  dataClass?: 'phi' | 'mock';
  createdAt: number;
};

/** A persisted trace row as the viewer receives it. */
export type ConnectorTraceRow = ConnectorTraceEntry & {
  id: number;
  /** Rollup key for recurring error conditions (error rows only). */
  dedupKey?: string | null;
  /** Times the condition recurred; `createdAt` reflects the latest. */
  occurrences?: number;
  /**
   * First-insert facts that SURVIVE rollup (`data`/`createdAt` are
   * overwritten per recurrence; this is not). Null on rows written before
   * the column existed — render '—', never backfill (the prune ages them out).
   * `mock` = written under the mock vendor; the nightly prune deletes these
   * regardless of age.
   */
  meta?: { insertAt?: number; mock?: boolean } | null;
};

/** Live-session marker for the current invocation's trace collector. */
export type ConnectorTraceSession = {
  vendor: KnownClearingHouses;
  until: number;
};

/**
 * Per-invocation trace accumulator. Created by `beginConnectorTrace` /
 * `resumeConnectorTraceFromRow` and threaded EXPLICITLY into `traceStep` /
 * `flushConnectorTrace`. There is deliberately NO module-level or GLOBALS trace
 * state: each invocation owns its own collector, so nothing can leak across warm
 * Lambda invocations or between concurrent requests that share a process. Treat
 * as opaque — only the trace helpers read its fields.
 */
export type ConnectorTraceCollector = {
  session: ConnectorTraceSession | null;
  entries: ConnectorTraceEntry[];
};
